import { useEffect, useRef, useState, useMemo } from "react";
import SiteNav from "@/components/SiteNav";
import SiteFooter from "@/components/SiteFooter";
import { api, tradeAuth, connectToDeriv, toastApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { streamCandles, streamTicks } from "@/lib/derivWs";
import { StakeSelector } from "@/components/casino/StakeSelector";
import { GUARD_PCT, TARGET_LOSS_RUN, survivableLosses, safeBaseFor, recoveryStake } from "@/lib/stakeMath";
import { createChart, CandlestickSeries, LineSeries, AreaSeries } from "lightweight-charts";
import {
  Crosshair, Minus, TrendingUp, Grid3x3, Square, ArrowUpCircle, ArrowDownCircle,
  Trash2, HelpCircle, Sliders, Clock, ChevronDown, Square as StopIcon,
} from "lucide-react";

const SYMBOLS = [
  { key: "R_10", label: "Volatility 10" },
  { key: "R_25", label: "Volatility 25" },
  { key: "R_50", label: "Volatility 50" },
  { key: "R_75", label: "Volatility 75" },
  { key: "R_100", label: "Volatility 100" },
  { key: "1HZ10V", label: "Volatility 10 (1s)" },
  { key: "1HZ50V", label: "Volatility 50 (1s)" },
  { key: "1HZ100V", label: "Volatility 100 (1s)" },
  { key: "frxEURUSD", label: "EUR/USD" },
  { key: "frxGBPUSD", label: "GBP/USD" },
  { key: "frxXAUUSD", label: "Gold" },
  { key: "frxUSDJPY", label: "USD/JPY" },
  { key: "cryBTCUSD", label: "Bitcoin" },
  { key: "cryETHUSD", label: "Ethereum" },
  { key: "BOOM1000", label: "Boom 1000" },
  { key: "CRASH1000", label: "Crash 1000" },
  { key: "JD25", label: "Jump 25" },
];

const TIMEFRAMES = [
  { k: "1m",  label: "1m",  seconds: 60 },
  { k: "2m",  label: "2m",  seconds: 120 },
  { k: "5m",  label: "5m",  seconds: 300 },
  { k: "15m", label: "15m", seconds: 900 },
  { k: "1h",  label: "1h",  seconds: 3600 },
  { k: "4h",  label: "4h",  seconds: 14400 },
  { k: "1D",  label: "1D",  seconds: 86400 },
];

const TOOLS = [
  { id: "select", label: "Select", icon: Crosshair },
  { id: "line",   label: "Line",   icon: Minus },
  { id: "trend",  label: "Trend",  icon: TrendingUp },
  { id: "fib",    label: "Fib",    icon: Grid3x3 },
  { id: "zone",   label: "Zone",   icon: Square },
  { id: "buy",    label: "Buy",    icon: ArrowUpCircle, accent: "success" },
  { id: "sell",   label: "Sell",   icon: ArrowDownCircle, accent: "danger" },
  { id: "clear",  label: "Clear",  icon: Trash2 },
];

function ticksToCandles(prices, times, bucketSec) {
  if (!prices.length) return [];
  const map = new Map();
  for (let i = 0; i < prices.length; i++) {
    const t = Number(times[i]);
    const p = Number(prices[i]);
    const bucket = Math.floor(t / bucketSec) * bucketSec;
    const c = map.get(bucket);
    if (!c) map.set(bucket, { time: bucket, open: p, high: p, low: p, close: p });
    else { c.high = Math.max(c.high, p); c.low = Math.min(c.low, p); c.close = p; }
  }
  return Array.from(map.values()).sort((a, b) => a.time - b.time);
}

export default function Charts() {
  const [symbol, setSymbol] = useState("1HZ100V");
  const [timeframe, setTimeframe] = useState("1m");
  const [chartType, setChartType] = useState("candles");
  const [tool, setTool] = useState("select");
  const [drawCount, setDrawCount] = useState(0);
  const [bottomTab, setBottomTab] = useState("risk"); // risk | risefall
  const [safeMode, setSafeMode] = useState(true);

  const [last, setLast] = useState(null);
  const [prev, setPrev] = useState(null);
  const [ohlc, setOhlc] = useState({ o: null, h: null, l: null, c: null });
  const [countdown, setCountdown] = useState("00:00");

  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const seriesRef = useRef(null);
  const bucketRef = useRef({ time: 0, open: 0, high: 0, low: 0, close: 0 });

  const [stake, setStake] = useState(() => Math.max(0.35, Number(localStorage.getItem("naevo_chart_stake")) || 0.35));
  const changeStake = (v) => { setStake(v); localStorage.setItem("naevo_chart_stake", String(v)); };
  const [botNextStake, setBotNextStake] = useState(null);
  const balanceNow = Number(localStorage.getItem("naevo_balance")) || 0;
  const maxSafeStake = balanceNow > 0 ? Number((balanceNow * GUARD_PCT).toFixed(2)) : null;
  const [ticks, setTicks] = useState(5);
  const [duration, setDuration] = useState(1);
  const [buying, setBuying] = useState(false);

  // active price lines (drawing tools) — we keep refs so we can remove them on Clear
  const priceLinesRef = useRef([]);

  // Open positions + trade history (local lifecycle, polled from the backend)
  const [openPositions, setOpenPositions] = useState([]);
  const [tradeHistory, setTradeHistory] = useState([]);
  const [historyTab, setHistoryTab] = useState("all"); // "all" | "this"

  // Auto-bot + lot-size guardrails
  const [autoBotRunning, setAutoBotRunning] = useState(false);
  const autoBotAbort = useRef(false);
  const guardAckRef = useRef(0);
  const [maxStake, setMaxStake] = useState(1.00);
  const [lossBudget, setLossBudget] = useState(10.00);     // $ today
  const [maxLossStreak, setMaxLossStreak] = useState(8);   // consecutive losses → auto-pause
  const [recoverySteps, setRecoverySteps] = useState(3);   // recovery multipliers before reset
  const [lockProfitPct, setLockProfitPct] = useState(50);  // lock % of peak profit (sim stop)
  // live tracking (today only, memoized from trade history)
  const guardStats = useMemo(() => {
    const todayStart = new Date(); todayStart.setHours(0,0,0,0);
    const today = tradeHistory.filter((t) => (t.ts || 0) >= todayStart.getTime() / 1000);
    const todaysLoss = today.reduce((s, t) => s + (Number(t.profit) < 0 ? Math.abs(Number(t.profit)) : 0), 0);
    const todaysProfit = today.reduce((s, t) => s + (Number(t.profit) > 0 ? Number(t.profit) : 0), 0);
    const net = todaysProfit - todaysLoss;
    let streak = 0;
    for (const t of tradeHistory) {
      if (t.won) break;
      streak += 1;
    }
    return { todaysLoss, todaysProfit, net, lossStreak: streak };
  }, [tradeHistory]);
  const filteredHistory = useMemo(() => (
    historyTab === "this" ? tradeHistory.filter((t) => t.symbol === symbol) : tradeHistory
  ), [tradeHistory, historyTab, symbol]);

  const symbolLabel = useMemo(() => SYMBOLS.find((s) => s.key === symbol)?.label || symbol, [symbol]);
  const bucketSec = useMemo(() => TIMEFRAMES.find((t) => t.k === timeframe)?.seconds || 60, [timeframe]);

  // Build / rebuild chart
  useEffect(() => {
    if (!containerRef.current) return;
    if (chartRef.current) {
      try { chartRef.current.remove(); } catch {}
      chartRef.current = null; seriesRef.current = null;
    }

    const chart = createChart(containerRef.current, {
      autoSize: true,
      localization: { locale: "en-US" },
      layout: {
        background: { type: "solid", color: "#07040E" },
        textColor: "rgba(255,255,255,0.55)",
        fontFamily: "'IBM Plex Mono', monospace",
      },
      grid: {
        vertLines: { color: "rgba(255,255,255,0.04)" },
        horzLines: { color: "rgba(255,255,255,0.04)" },
      },
      rightPriceScale: { borderColor: "rgba(255,255,255,0.08)", scaleMargins: { top: 0.08, bottom: 0.08 } },
      timeScale: {
        borderColor: "rgba(255,255,255,0.08)",
        timeVisible: true,
        secondsVisible: timeframe === "1m",
        barSpacing: 5,       // thin candles like a real market feed
        minBarSpacing: 2,
        rightOffset: 6,      // leave a little room past the latest bar
      },
      crosshair: {
        mode: 1,
        vertLine: { color: "rgba(232,181,74,0.4)", width: 1, style: 3, labelBackgroundColor: "#E8B54A" },
        horzLine: { color: "rgba(232,181,74,0.4)", width: 1, style: 3, labelBackgroundColor: "#E8B54A" },
      },
      handleScroll: { mouseWheel: true, pressedMouseMove: true, horzTouchDrag: true, vertTouchDrag: false },
      handleScale:  { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
    });

    let series;
    if (chartType === "candles") {
      series = chart.addSeries(CandlestickSeries, {
        upColor: "#00FF9D", downColor: "#FF3366",
        borderUpColor: "#00FF9D", borderDownColor: "#FF3366",
        wickUpColor: "#00FF9D", wickDownColor: "#FF3366",
      });
    } else if (chartType === "line") {
      series = chart.addSeries(LineSeries, { color: "#E8B54A", lineWidth: 2 });
    } else {
      series = chart.addSeries(AreaSeries, {
        lineColor: "#E8B54A", topColor: "rgba(232,181,74,0.3)", bottomColor: "rgba(232,181,74,0.0)", lineWidth: 2,
      });
    }

    chartRef.current = chart;
    seriesRef.current = series;

    let cancelled = false;
    let lastTime = 0;
    let prevClose = null;
    const showRecent = (data) => {
      const first = data[Math.max(0, data.length - 120)].time;
      chart.timeScale().setVisibleRange({ from: first, to: data[data.length - 1].time });
    };
    const onPrice = (p) => {
      if (cancelled) return;
      setLast((l) => { if (l !== null && l !== p) prevClose = l; return p; });
      if (prevClose !== null) setPrev(prevClose);
    };
    let stop;
    if (chartType === "candles") {
      stop = streamCandles(symbol, bucketSec, (candles) => {
        if (cancelled || !candles.length) return;
        series.setData(candles);
        lastTime = candles[candles.length - 1].time;
        const b = candles[candles.length - 1];
        bucketRef.current = { ...b };
        setOhlc({ o: b.open, h: b.high, l: b.low, c: b.close });
        setPrev(candles[candles.length - 2]?.close ?? b.close);
        setLast(b.close);
        showRecent(candles);
      }, (bar) => {
        if (cancelled || bar.time < lastTime) return;
        lastTime = bar.time;
        bucketRef.current = bar;
        series.update(bar);
        setOhlc({ o: bar.open, h: bar.high, l: bar.low, c: bar.close });
        onPrice(bar.close);
      }, (err) => toast.error(err.message || "Market data unavailable"));
    } else {
      stop = streamTicks(symbol, (pts) => {
        if (cancelled || !pts.length) return;
        series.setData(pts);
        lastTime = pts[pts.length - 1].time;
        setPrev(pts[pts.length - 2]?.value ?? pts[pts.length - 1].value);
        setLast(pts[pts.length - 1].value);
        showRecent(pts);
      }, (pt) => {
        if (cancelled || pt.time <= lastTime) return;
        lastTime = pt.time;
        series.update(pt);
        onPrice(pt.value);
      }, (err) => toast.error(err.message || "Market data unavailable"));
    }

    return () => {
      cancelled = true;
      stop && stop();
      try { chart.remove(); } catch {}
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, timeframe, chartType]);

  // Countdown to next bar close
  useEffect(() => {
    const tick = () => {
      const now = Math.floor(Date.now() / 1000);
      const next = Math.ceil(now / bucketSec) * bucketSec;
      const rem = next - now;
      const m = Math.floor(rem / 60), s = rem % 60;
      setCountdown(`${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [bucketSec]);

  const change = last && prev ? ((last - prev) / prev) * 100 : 0;

  const session_id = typeof window !== "undefined" ? localStorage.getItem("naevo_session") : null;

  const settleContract = async (contractId) => {
    // poll until the contract settles, then move it from Open → History
    for (let i = 0; i < 180; i++) {  // up to ~6 minutes
      await new Promise((r) => setTimeout(r, 2000));
      try {
        const r = await api.get(`/chart/trade/${contractId}`, { params: { session_id: tradeAuth()?.session_id } });
        const data = r.data || {};
        if (data.settled) {
          setOpenPositions((xs) => xs.filter((p) => p.contract_id !== contractId));
          setTradeHistory((xs) => [{
            contract_id: contractId,
            symbol: data.symbol,
            direction: data.direction,
            stake: data.stake,
            profit: data.profit,
            won: data.won,
            mode: data.mode,
            entry_price: data.entry_price,
            exit_price: data.exit_price,
            exit_time: data.exit_time,
            ts: Math.floor(Date.now() / 1000),
          }, ...xs].slice(0, 40));
          if (data.won) toast.success(`Contract WON · +$${Number(data.profit).toFixed(2)} (${data.direction.toUpperCase()})`);
          else toast.error(`Contract LOST · -$${Number(data.stake).toFixed(2)} (${data.direction.toUpperCase()})`);
          return data;
        } else {
          // keep progress fresh on the open-position card
          setOpenPositions((xs) => xs.map((p) => p.contract_id === contractId ? { ...p, progress: data.progress ?? p.progress } : p));
        }
      } catch (e) {
        // backend hiccup — try again
      }
    }
    return null;
  };

  const placeTrade = async (dir, stakeOverride) => {
    if (buying) return;
    const auth = tradeAuth();
    if (!auth) {
      toast.error("Login with Deriv to trade", { action: { label: "Login", onClick: () => connectToDeriv() } });
      return;
    }
    const stakeNum = Math.max(0.35, Number(stakeOverride ?? stake) || 0.35);
    if (stakeOverride === undefined && maxSafeStake !== null && stakeNum > maxSafeStake && guardAckRef.current !== stakeNum) {
      toast.warning(`Max Bet Guard: $${stakeNum.toFixed(2)} is over 25% of your $${balanceNow.toFixed(2)} balance`, {
        action: { label: "Trade anyway", onClick: () => { guardAckRef.current = stakeNum; } }, duration: 6000,
      });
      return null;
    }
    const durationNum = Math.max(1, Math.min(60, parseInt(duration, 10) || 1));
    setBuying(true);
    try {
      const payload = {
        symbol,
        stake: stakeNum,
        duration_min: durationNum,
        direction: dir,  // "rise" | "fall"
        session_id: auth.session_id,
        account_id: auth.account_id,
      };
      const r = await api.post("/chart/trade", payload);
      const data = r.data || {};
      const contractId = data.contract_id;
      setOpenPositions((xs) => [{
        contract_id: contractId,
        symbol: data.symbol,
        direction: data.direction,
        stake: data.stake,
        duration_sec: data.duration_sec,
        entry_time: data.entry_time,
        settle_at: data.settle_at,
        entry_price: data.entry_price,
        mode: data.mode,
        progress: 0,
      }, ...xs]);
      toast.success(`${dir === "rise" ? "▲ RISE" : "▼ FALL"} · $${data.stake ?? stakeNum} / ${Math.round((data.duration_sec || durationNum * 60) / 60)}m · ${data.account_type === "demo" ? "DEMO" : "REAL"} ${data.account_id}`);
      settleContract(contractId);
      return contractId;
    } catch (e) {
      toastApiError(toast, e, "Trade failed");
    } finally {
      setBuying(false);
    }
  };

  // Auto-bot: pull /chart/signal → place that direction → wait for settlement → repeat
  // Respects the Lot-Size tool: clamps stake to maxStake, auto-stops when today's loss budget
  // is reached or after `maxLossStreak` consecutive losses.
  const startAutoBot = async () => {
    if (autoBotRunning) return;
    autoBotAbort.current = false;
    setAutoBotRunning(true);
    // Base stake (Safe Mode caps the BASE at $1); losses recover with one win, then back to base.
    const base = Math.max(0.35, Math.min(Number(maxStake) || stake, Number(stake) || 0.35, safeMode ? 1 : Infinity));
    let hole = 0;
    let streak = 0;
    toast.success(`Auto-bot armed on ${symbolLabel} · base $${base.toFixed(2)}`);
    try {
      while (!autoBotAbort.current) {
        if (streak >= Number(maxLossStreak || 0) && Number(maxLossStreak || 0) > 0) {
          toast.error(`Auto-bot paused · ${streak} losses in a row`);
          break;
        }
        // GUARDRAIL 1 — today's loss budget
        if (guardStats.todaysLoss >= Number(lossBudget || 0) && Number(lossBudget || 0) > 0) {
          toast.error(`Auto-bot paused · daily loss budget $${Number(lossBudget).toFixed(2)} reached`);
          break;
        }
        // GUARDRAIL 2 — consecutive losses
        if (guardStats.lossStreak >= Number(maxLossStreak || 0) && Number(maxLossStreak || 0) > 0) {
          toast.error(`Auto-bot paused · ${guardStats.lossStreak} losses in a row`);
          break;
        }
        let dir = "rise";
        try {
          const s = await api.get("/chart/signal", { params: { symbol } });
          dir = s.data?.direction || "rise";
        } catch {}
        if (autoBotAbort.current) break;
        const next = recoveryStake(base, hole);
        setBotNextStake(next);
        const cid = await placeTrade(dir, next);
        if (!cid) break;
        // wait for THIS contract to settle before placing the next (keeps polling even if STOP is hit)
        for (let i = 0; i < 180; i++) {
          await new Promise((r) => setTimeout(r, 2000));
          try {
            const st = await api.get(`/chart/trade/${cid}`, { params: { session_id: tradeAuth()?.session_id } });
            if (st.data?.settled) {
              if (st.data.won) { hole = 0; streak = 0; } else { hole = Number((hole + next).toFixed(2)); streak += 1; }
              break;
            }
          } catch {}
        }
        // brief cooldown
        if (!autoBotAbort.current) await new Promise((r) => setTimeout(r, 1500));
      }
    } finally {
      setAutoBotRunning(false);
      setBotNextStake(null);
      toast("Auto-bot stopped");
    }
  };

  const stopAutoBot = () => { autoBotAbort.current = true; };

  const handleTool = (id) => {
    const series = seriesRef.current;
    const addLine = (price, color, title, lineStyle = 0) => {
      if (!series || typeof price !== "number") return null;
      const pl = series.createPriceLine({ price, color, lineWidth: 1, lineStyle, axisLabelVisible: true, title });
      priceLinesRef.current.push(pl);
      return pl;
    };
    if (id === "clear") {
      priceLinesRef.current.forEach((pl) => { try { series?.removePriceLine(pl); } catch {} });
      priceLinesRef.current = [];
      setDrawCount(0); setTool("select"); toast("Drawings cleared"); return;
    }
    if (id === "buy" || id === "sell") { placeTrade(id === "buy" ? "rise" : "fall"); return; }
    setTool(id);
    if (last == null) { toast.error("Price not loaded yet"); return; }
    if (id === "select") return;
    if (id === "line")  { addLine(last, "#F5A623", `LINE ${last.toFixed(2)}`, 0); setDrawCount((n) => n + 1); toast(`Line @ ${last.toFixed(2)}`); }
    if (id === "trend") { addLine(last, "#4ADE9B", `TREND ${last.toFixed(2)}`, 2); setDrawCount((n) => n + 1); toast(`Trend @ ${last.toFixed(2)}`); }
    if (id === "fib") {
      // 38.2%, 50%, 61.8% retracement over the last 30-bar range
      const hi = (ohlc.h || last) * 1.004;
      const lo = (ohlc.l || last) * 0.996;
      const range = hi - lo;
      [{ p: lo + range * 0.382, t: "0.382" }, { p: lo + range * 0.5, t: "0.500" }, { p: lo + range * 0.618, t: "0.618" }]
        .forEach(({ p, t }) => addLine(Number(p.toFixed(4)), "#B28DFF", `FIB ${t}`, 3));
      setDrawCount((n) => n + 3); toast("Fib retracement dropped");
    }
    if (id === "zone") {
      const hi = last * 1.002, lo = last * 0.998;
      addLine(Number(hi.toFixed(4)), "#06b6d4", `ZONE HI`, 0);
      addLine(Number(lo.toFixed(4)), "#06b6d4", `ZONE LO`, 0);
      setDrawCount((n) => n + 2); toast(`Zone ${lo.toFixed(2)} → ${hi.toFixed(2)}`);
    }
  };

  return (
    <div>
      <SiteNav />
      <main className="mx-auto max-w-7xl px-4 py-4">

        {/* --- TOP TOOLBAR --- */}
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={symbol} onValueChange={setSymbol}>
            <SelectTrigger className="w-52 bg-transparent border-white/10 h-9" data-testid="symbol-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SYMBOLS.map((s) => (
                <SelectItem key={s.key} value={s.key} data-testid={`symbol-opt-${s.key}`}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-1" data-testid="tf-row">
            {TIMEFRAMES.map((t) => (
              <button
                key={t.k}
                onClick={() => setTimeframe(t.k)}
                data-testid={`tf-${t.k}`}
                className={`px-3 py-1.5 text-xs font-mono-naevo rounded-md ${timeframe === t.k ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "text-white/60 hover:text-white border border-transparent"}`}
              >
                {t.label}
              </button>
            ))}
            <button className="px-3 py-1.5 text-xs font-mono-naevo text-white/60 hover:text-white rounded-md border border-transparent flex items-center gap-1">
              More <ChevronDown className="h-3 w-3" />
            </button>
          </div>

          <div className="ml-2 flex items-center gap-1">
            <button onClick={() => setChartType("candles")} data-testid="type-candles" className={`px-3 py-1.5 text-xs font-mono-naevo rounded-md border ${chartType === "candles" ? "border-white/40 bg-white/5 text-white" : "border-transparent text-white/60 hover:text-white"}`}>Candles</button>
            <button onClick={() => setChartType("line")}    data-testid="type-line"    className={`px-3 py-1.5 text-xs font-mono-naevo rounded-md border ${chartType === "line"    ? "border-white/40 bg-white/5 text-white" : "border-transparent text-white/60 hover:text-white"}`}>Line</button>
            <button onClick={() => setChartType("area")}    data-testid="type-area"    className={`px-3 py-1.5 text-xs font-mono-naevo rounded-md border ${chartType === "area"    ? "border-white/40 bg-white/5 text-white" : "border-transparent text-white/60 hover:text-white"}`}>Area</button>
          </div>

          <div className="ml-auto">
            <button className="px-3 py-1.5 text-xs font-mono-naevo rounded-md border border-cyan-400/40 text-cyan-300 flex items-center gap-1" data-testid="tour-btn">
              <HelpCircle className="h-3.5 w-3.5" /> Tour
            </button>
          </div>
        </div>

        {/* --- SECONDARY ROW: INDICATORS · DRAWINGS · COUNTDOWN · OHLC --- */}
        <div className="mt-2 flex items-center gap-3 flex-wrap text-xs font-mono-naevo">
          <button className="px-3 py-1.5 rounded-md border border-white/10 text-white/80 flex items-center gap-1.5 hover:border-white/30" data-testid="indicators-btn">
            <Sliders className="h-3 w-3" /> Indicators <span className="text-white/40">({drawCount})</span>
          </button>
          <div className="text-white/40 uppercase tracking-widest text-[10px]">
            <span className="text-white/60">{drawCount}</span> drawings saved
          </div>
          <div className="flex items-center gap-1 text-[#F5A623]">
            <Clock className="h-3 w-3" /> <span data-testid="chart-countdown">{countdown}</span>
          </div>
          <div className="ml-auto flex items-center gap-3 text-white/60">
            <span>O <span className="text-white">{ohlc.o?.toFixed(2) ?? "—"}</span></span>
            <span>H <span className="text-[color:var(--naevo-success)]">{ohlc.h?.toFixed(2) ?? "—"}</span></span>
            <span>L <span className="text-[color:var(--naevo-danger)]">{ohlc.l?.toFixed(2) ?? "—"}</span></span>
            <span>C <span className="text-white">{ohlc.c?.toFixed(2) ?? "—"}</span></span>
            {change !== 0 && (
              <span className={change >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}>
                {change >= 0 ? "+" : ""}{change.toFixed(3)}%
              </span>
            )}
          </div>
        </div>

        {/* --- CHART + LEFT RAIL --- */}
        <div className="mt-3 rounded-2xl border border-white/10 bg-[#07040E] overflow-hidden flex">
          <div className="w-14 border-r border-white/5 py-3 flex flex-col items-center gap-1 shrink-0" data-testid="tool-rail">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const active = tool === t.id;
              const accent =
                t.accent === "success" ? "text-[color:var(--naevo-success)] hover:bg-[color:var(--naevo-success)]/10" :
                t.accent === "danger"  ? "text-[color:var(--naevo-danger)] hover:bg-[color:var(--naevo-danger)]/10" :
                active ? "text-cyan-300 bg-cyan-400/10" : "text-white/60 hover:text-white hover:bg-white/5";
              return (
                <button
                  key={t.id}
                  onClick={() => handleTool(t.id)}
                  data-testid={`tool-${t.id}`}
                  className={`w-11 h-14 rounded-md flex flex-col items-center justify-center gap-0.5 transition-colors ${accent}`}
                >
                  <Icon className="h-4 w-4" />
                  <span className="text-[9px] font-mono-naevo uppercase">{t.label}</span>
                </button>
              );
            })}
          </div>
          <div className="flex-1 relative">
            <div ref={containerRef} className="w-full" style={{ height: 540 }} data-testid="tv-chart" />
            {/* overlay: current price pink + countdown orange on the right axis */}
            {last !== null && seriesRef.current?.priceToCoordinate(last) != null && (
              <>
                <div className="absolute right-0 z-10 pointer-events-none text-[11px] font-mono-naevo bg-[#F5A623] text-black px-2 py-0.5 rounded-l" style={{ top: seriesRef.current.priceToCoordinate(last) + 12 }} data-testid="countdown-badge">
                  {countdown}
                </div>
              </>
            )}
          </div>
        </div>

        {/* --- RISK:REWARD / RISE:FALL TABS --- */}
        <div className="mt-4 rounded-xl border border-white/10 bg-[#0C0914] overflow-hidden" data-testid="bottom-tabs">
          <div className="grid grid-cols-2">
            <button
              onClick={() => setBottomTab("risk")}
              data-testid="tab-risk"
              className={`py-3 text-xs font-mono-naevo tracking-widest uppercase ${bottomTab === "risk" ? "bg-white/5 text-white border-b-2 border-cyan-400" : "text-white/50 hover:text-white"}`}
            >
              Risk : Reward
            </button>
            <button
              onClick={() => setBottomTab("risefall")}
              data-testid="tab-risefall"
              className={`py-3 text-xs font-mono-naevo tracking-widest uppercase ${bottomTab === "risefall" ? "bg-white/5 text-white border-b-2 border-cyan-400" : "text-white/50 hover:text-white"}`}
            >
              Rise / Fall
            </button>
          </div>

          {bottomTab === "risefall" ? (
            <div className="p-5 space-y-4" data-testid="rise-fall-card">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <StakeSelector stake={Number(stake)} onChange={changeStake} disabled={buying || autoBotRunning} maxSafe={maxSafeStake}
                    nextStake={autoBotRunning ? botNextStake : null}
                    survive={balanceNow > 0 ? survivableLosses(Number(stake), balanceNow) : null} target={TARGET_LOSS_RUN}
                    safeBase={balanceNow > 0 ? safeBaseFor(balanceNow) : null} />
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">Duration (min)</div>
                  <input
                    type="number" value={duration} min="1" max="60" step="1" inputMode="numeric"
                    onChange={(e) => setDuration(e.target.value)}
                    onBlur={(e) => {
                      const n = parseInt(e.target.value, 10);
                      if (!Number.isFinite(n) || n < 1) setDuration(1);
                      else if (n > 60) setDuration(60);
                      else setDuration(n);
                    }}
                    className="w-full bg-transparent border border-white/15 rounded-lg px-4 py-3 font-mono-naevo text-lg mt-2 focus:border-cyan-400 outline-none"
                    data-testid="duration-input"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => placeTrade("rise")} disabled={buying}
                  data-testid="rise-btn"
                  className="py-5 rounded-xl bg-[color:var(--naevo-success)] text-black font-mono-naevo text-lg flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60"
                  style={{ boxShadow: "0 0 32px rgba(0,255,157,0.3)" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18"/><polyline points="17 6 23 6 23 12"/></svg>
                  RISE
                </button>
                <button
                  onClick={() => placeTrade("fall")} disabled={buying}
                  data-testid="fall-btn"
                  className="py-5 rounded-xl bg-[color:var(--naevo-danger)] text-white font-mono-naevo text-lg flex items-center justify-center gap-2 hover:brightness-110 transition-all disabled:opacity-60"
                  style={{ boxShadow: "0 0 32px rgba(255,51,102,0.3)" }}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 18 13.5 8.5 8.5 13.5 1 6"/><polyline points="17 18 23 18 23 12"/></svg>
                  FALL
                </button>
              </div>

              <button
                onClick={() => autoBotRunning ? stopAutoBot() : startAutoBot()}
                data-testid="autobot-cta"
                className={`w-full py-3.5 rounded-xl font-mono-naevo text-sm flex items-center justify-center gap-2 transition-colors border ${
                  autoBotRunning
                    ? "border-[color:var(--naevo-danger)] bg-[color:var(--naevo-danger)]/10 text-[color:var(--naevo-danger)] hover:bg-[color:var(--naevo-danger)]/20"
                    : "border-cyan-400/40 bg-cyan-400/5 text-cyan-300 hover:bg-cyan-400/10"
                }`}
              >
                {autoBotRunning ? (
                  <>
                    <StopIcon className="h-4 w-4 fill-current" /> STOP AUTO-BOT
                  </>
                ) : (
                  <>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="10" rx="2"/><circle cx="12" cy="5" r="2"/><path d="M12 7v4"/><line x1="8" y1="16" x2="8" y2="16"/><line x1="16" y1="16" x2="16" y2="16"/></svg>
                    RUN AN AUTO-BOT ON THIS MARKET
                  </>
                )}
              </button>
            </div>
          ) : (
            <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm" data-testid="risk-reward-card">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Risk per trade</div>
                <div className="font-mono-naevo text-xl mt-1 text-[color:var(--naevo-danger)]">${stake.toFixed(2)}</div>
                <div className="text-[10px] text-white/40 mt-1">Max loss if contract expires out of the money</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Potential payout</div>
                <div className="font-mono-naevo text-xl mt-1 text-[color:var(--naevo-success)]">${(stake * 1.95).toFixed(2)}</div>
                <div className="text-[10px] text-white/40 mt-1">Net profit ${(stake * 0.95).toFixed(2)} on a win</div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-white/40">Ratio</div>
                <div className="font-mono-naevo text-xl mt-1 text-magenta">1 : {(0.95).toFixed(2)}</div>
                <div className="text-[10px] text-white/40 mt-1">Reward vs risk per round</div>
              </div>
            </div>
          )}
        </div>

        {/* --- PENDING ORDERS --- */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid="pending-orders">
          <div className="flex items-center gap-2 text-cyan-300 text-xs font-mono-naevo uppercase tracking-widest">
            <Clock className="h-3.5 w-3.5" /> Pending orders
          </div>
          <div className="mt-6 text-center text-sm text-white/40">No pending orders</div>
        </div>

        {/* --- OPEN POSITIONS --- */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid="open-positions">
          <div className="flex items-center justify-between">
            <div className="text-xs font-mono-naevo uppercase tracking-widest text-white">Open positions</div>
            <div className="text-[10px] font-mono-naevo text-white/40">{openPositions.length} active</div>
          </div>
          {openPositions.length === 0 ? (
            <div className="mt-6 text-center text-sm text-white/40">No open positions</div>
          ) : (
            <div className="mt-3 space-y-2">
              {openPositions.map((p) => {
                const now = Math.floor(Date.now() / 1000);
                const prog = Math.max(0, Math.min(1, (now - p.entry_time) / p.duration_sec));
                const rem = Math.max(0, p.settle_at - now);
                return (
                  <div key={p.contract_id} className="rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid={`open-${p.contract_id}`}>
                    <div className="flex items-center justify-between flex-wrap gap-2 text-sm">
                      <div className="flex items-center gap-2 font-mono-naevo">
                        <span className={p.direction === "rise" ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}>
                          {p.direction === "rise" ? "▲ RISE" : "▼ FALL"}
                        </span>
                        <span className="text-white/60">·</span>
                        <span className="text-white/70">{p.symbol}</span>
                        <span className="text-white/60">·</span>
                        <span className="text-white/70">${Number(p.stake).toFixed(2)}</span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${p.mode === "live" ? "bg-cyan-400/15 text-cyan-300 border border-cyan-400/40" : "bg-white/5 text-white/50 border border-white/10"}`}>
                          {p.mode === "live" ? "LIVE" : "DEMO"}
                        </span>
                      </div>
                      <div className="text-xs font-mono-naevo text-white/60">
                        settles in <span className="text-white">{Math.floor(rem/60)}:{String(rem%60).padStart(2,"0")}</span>
                      </div>
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-white/5 rounded-full overflow-hidden">
                      <div className="h-full bg-cyan-400 transition-all" style={{ width: `${prog * 100}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* --- TRADE HISTORY --- */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid="trade-history">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2 text-cyan-300 text-xs font-mono-naevo uppercase tracking-widest">
              <Clock className="h-3.5 w-3.5" /> Trade history · {historyTab === "this" ? symbolLabel : "All markets"}
            </div>
            <div className="flex items-center gap-2 text-xs font-mono-naevo">
              <button
                onClick={() => setHistoryTab("all")}
                data-testid="history-tab-all"
                className={`px-3 py-1 rounded-md border ${historyTab === "all" ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "text-white/50 border-white/10 hover:text-white"}`}
              >All markets</button>
              <button
                onClick={() => setHistoryTab("this")}
                data-testid="history-tab-this"
                className={`px-3 py-1 rounded-md border ${historyTab === "this" ? "bg-cyan-400/15 text-cyan-300 border-cyan-400/40" : "text-white/50 border-white/10 hover:text-white"}`}
              >This market</button>
              <a href="/history" className="px-3 py-1 rounded-md text-cyan-300 hover:underline">ALL HISTORY →</a>
            </div>
          </div>
          {filteredHistory.length === 0 ? (
            <div className="mt-6 text-center text-sm text-white/40">{historyTab === "this" ? `No trades on ${symbolLabel} yet` : "No trades yet · place one to seed your history"}</div>
          ) : (
            <div className="mt-3 space-y-1">
              {filteredHistory.map((t) => (
                <div key={t.contract_id} className="rounded-md border border-white/5 bg-white/[0.02] px-3 py-2 flex items-center justify-between text-xs font-mono-naevo">
                  <div className="flex items-center gap-2">
                    <span className={t.direction === "rise" ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}>
                      {t.direction === "rise" ? "▲" : "▼"}
                    </span>
                    <span className="text-white/70">{t.symbol}</span>
                    <span className="text-white/40">·</span>
                    <span className="text-white/60">${Number(t.stake).toFixed(2)}</span>
                  </div>
                  <div className={`font-mono-naevo ${t.won ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}`}>
                    {t.won ? "+" : ""}${Number(t.profit).toFixed(2)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* --- AUTO LOT-SIZE TOOL (guardrails for RUN AUTO-BOT) --- */}
        <div className="mt-4 rounded-2xl border border-white/10 bg-[#0C0914] p-5" data-testid="auto-lot">
          <div className="flex items-center gap-2 text-[#F5A623] text-xs font-mono-naevo uppercase tracking-widest">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="22"/><path d="M4 7h16l-4 10H8L4 7z"/></svg>
            Auto lot-size tool
          </div>

          <div className="mt-4 rounded-xl border border-white/10 bg-black/30 p-4 space-y-3">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-2">
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#00FF9D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
                <span className="font-mono-naevo text-sm">SAFE MODE</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full ${safeMode ? "bg-[color:var(--naevo-success)]/15 text-[color:var(--naevo-success)] border border-[color:var(--naevo-success)]/40" : "bg-white/5 text-white/40 border border-white/10"}`}>{safeMode ? "ON · caps stake at $1" : "OFF"}</span>
              </div>
              <button
                onClick={() => setSafeMode((v) => !v)}
                data-testid="safe-mode-toggle"
                className={`relative h-7 w-12 rounded-full transition-colors ${safeMode ? "bg-[color:var(--naevo-success)]" : "bg-white/15"}`}
              >
                <span className={`absolute top-0.5 h-6 w-6 rounded-full bg-white transition-all ${safeMode ? "left-5" : "left-0.5"}`} />
              </button>
            </div>

            {/* 4 editable guardrail inputs */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              <label className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">Max stake</div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-white/60 text-xs">$</span>
                  <input
                    type="number" step="0.05" min="0.35" max="100"
                    value={String(maxStake)}
                    onChange={(e) => setMaxStake(Math.max(0.35, Math.min(100, Number(e.target.value) || 0.35)))}
                    data-testid="max-stake-input"
                    className="w-full bg-transparent text-sm font-mono-naevo text-white outline-none"
                  />
                </div>
              </label>
              <label className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">Loss budget / day</div>
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-white/60 text-xs">$</span>
                  <input
                    type="number" step="1" min="0" max="10000"
                    value={String(lossBudget)}
                    onChange={(e) => setLossBudget(Math.max(0, Number(e.target.value) || 0))}
                    data-testid="loss-budget-input"
                    className="w-full bg-transparent text-sm font-mono-naevo text-white outline-none"
                  />
                </div>
              </label>
              <label className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">Pause after X losses</div>
                <input
                  type="number" step="1" min="1" max="20"
                  value={String(maxLossStreak)}
                  onChange={(e) => setMaxLossStreak(Math.max(1, Math.min(20, Number(e.target.value) || 1)))}
                  data-testid="max-losses-input"
                  className="w-full bg-transparent text-sm font-mono-naevo text-white outline-none mt-1"
                />
              </label>
              <label className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2">
                <div className="text-[10px] uppercase tracking-widest text-white/40 font-mono-naevo">Recovery steps</div>
                <input
                  type="number" step="1" min="0" max="10"
                  value={String(recoverySteps)}
                  onChange={(e) => setRecoverySteps(Math.max(0, Math.min(10, Number(e.target.value) || 0)))}
                  data-testid="recovery-steps-input"
                  className="w-full bg-transparent text-sm font-mono-naevo text-white outline-none mt-1"
                />
              </label>
            </div>

            {/* live stats row */}
            <div className="mt-1 flex items-center justify-between text-xs font-mono-naevo text-white/70 flex-wrap gap-2">
              <span className="uppercase tracking-widest text-white/40">Today</span>
              <span>
                loss <span className={`${guardStats.todaysLoss >= lossBudget && lossBudget > 0 ? "text-[color:var(--naevo-danger)]" : "text-white"}`}>${guardStats.todaysLoss.toFixed(2)}</span> / ${Number(lossBudget).toFixed(2)}
                <span className="text-white/30 mx-2">·</span>
                net <span className={guardStats.net >= 0 ? "text-[color:var(--naevo-success)]" : "text-[color:var(--naevo-danger)]"}>{guardStats.net >= 0 ? "+" : ""}${guardStats.net.toFixed(2)}</span>
                <span className="text-white/30 mx-2">·</span>
                streak <span className={guardStats.lossStreak >= maxLossStreak && maxLossStreak > 0 ? "text-[color:var(--naevo-danger)]" : "text-white"}>{guardStats.lossStreak}L</span>
              </span>
            </div>
            <div className="mt-2 text-[11px] text-white/40 font-mono-naevo">
              Auto-bot clamps stake to <span className="text-white">${Number(safeMode ? Math.min(maxStake, 1) : maxStake).toFixed(2)}</span> · pauses when today's loss hits <span className="text-white">${Number(lossBudget).toFixed(2)}</span> or after <span className="text-white">{maxLossStreak}</span> straight losses · <span className="text-white">{recoverySteps}</span> recovery steps · locks <span className="text-white">{lockProfitPct}%</span> of peak profit.
            </div>
          </div>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
